/**
 * Central Communications & Activity Tracking Store
 * Tracks calls and WhatsApp messages made by Admins and RMs to retailers.
 */

export type CommunicationType = 'CALL' | 'WHATSAPP';
export type CommunicationRole = 'ADMIN' | 'RM' | 'SYSTEM';
export type CallOutcome =
  | 'CONNECTED'
  | 'ORDER_PLACED'
  | 'RATES_SHARED'
  | 'BUSY_CALLBACK'
  | 'NOT_REACHABLE'
  | 'WRONG_NUMBER'
  | 'GENERAL_QUERY';

export interface CommunicationRecord {
  id: string;
  retailerId: number;
  retailerName?: string;
  retailerPhone?: string;
  type: CommunicationType;
  direction: 'OUTBOUND' | 'INBOUND';
  senderName: string;
  senderRole: CommunicationRole;
  senderId?: string;
  timestamp: string; // ISO 8601
  // WhatsApp specific
  templateKey?: string;
  templateName?: string;
  messageText?: string;
  // Call specific
  callOutcome?: CallOutcome;
  notes?: string;
  durationSeconds?: number;
}

export interface CommunicationStats {
  totalCalls: number;
  adminCalls: number;
  rmCalls: number;
  totalWhatsApp: number;
  adminWhatsApp: number;
  rmWhatsApp: number;
  lastContactedAt?: string;
}

const STORAGE_KEY = 'mydailydirect.communications.v2';

/** Initial seed data so retailers have meaningful conversation history */
const INITIAL_SEED_COMMUNICATIONS: CommunicationRecord[] = [
  {
    id: 'comm-seed-1',
    retailerId: 1,
    retailerName: 'Sri Balaji Traders',
    retailerPhone: '9845012345',
    type: 'WHATSAPP',
    direction: 'OUTBOUND',
    senderName: 'Vignesh (Admin)',
    senderRole: 'ADMIN',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    templateKey: 'PRICE_UPDATE',
    templateName: "Today's Wholesale Prices",
    messageText: "Dear Sir/Madam,\n\nToday's Prices:\n- Rice – Per 26 kg Bag: ₹1,180\n- Toor Dal Broken – Per Kg: ₹142\n- Urad Dal Split – Per Kg: ₹128\n- Urad Dal Gota – Per Kg: ₹138\n- Fried Gram – Per Kg: ₹98\n\nPlace your orders:\n🌐 mydailydirect.in\n📲 WhatsApp: 9180348180\n\nTeam My Daily Direct",
  },
  {
    id: 'comm-seed-2',
    retailerId: 1,
    retailerName: 'Sri Balaji Traders',
    retailerPhone: '9845012345',
    type: 'CALL',
    direction: 'OUTBOUND',
    senderName: 'Ramesh Kumar (RM)',
    senderRole: 'RM',
    timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    callOutcome: 'ORDER_PLACED',
    durationSeconds: 145,
    notes: 'Discussed weekly rice & toor dal replenishment. Customer placed order for 10 bags Sona Masoori & 2 bags Toor Dal.',
  },
  {
    id: 'comm-seed-3',
    retailerId: 1,
    retailerName: 'Sri Balaji Traders',
    retailerPhone: '9845012345',
    type: 'WHATSAPP',
    direction: 'OUTBOUND',
    senderName: 'Ramesh Kumar (RM)',
    senderRole: 'RM',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    templateKey: 'ONBOARDING',
    templateName: 'Welcome Onboarding',
    messageText: "Welcome to My Daily Direct!\n\nLogin to mydailydirect.in to check today's prices and place your orders.\n\n🚚 Delivery Schedule – 560068\nEvery Monday & Thursday.\n\nFor more details or assistance, WhatsApp us:\n📲 9180348180\n\nHappy Shopping!\nTeam My Daily Direct",
  },
  {
    id: 'comm-seed-4',
    retailerId: 2,
    retailerName: 'Lakshmi Provision Store',
    retailerPhone: '9845098765',
    type: 'CALL',
    direction: 'OUTBOUND',
    senderName: 'Vignesh (Admin)',
    senderRole: 'ADMIN',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    callOutcome: 'RATES_SHARED',
    durationSeconds: 90,
    notes: 'Informed about sudden drop in refined sunflower oil rates (₹1,580/tin). Customer requested WhatsApp catalog link.',
  },
  {
    id: 'comm-seed-5',
    retailerId: 2,
    retailerName: 'Lakshmi Provision Store',
    retailerPhone: '9845098765',
    type: 'WHATSAPP',
    direction: 'OUTBOUND',
    senderName: 'Vignesh (Admin)',
    senderRole: 'ADMIN',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    templateKey: 'PROMOTE_RICE',
    templateName: 'Special Rice Prices',
    messageText: "Dear Sir/Madam,\n\n🍚 Today's Special Rice Prices!\n\n| Rice Variety | Price (26 kg Bag) -\n• Sona Masoori Raw: ₹1,180\n• Bullet Rice: ₹1,420\n• Steam Rice: ₹1,090\n\nOrder now at:\n🌐 mydailydirect.in\n📲 WhatsApp: 9180348180\n\nTeam My Daily Direct",
  },
];

function loadFromStorage(): CommunicationRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_SEED_COMMUNICATIONS));
      return INITIAL_SEED_COMMUNICATIONS;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return INITIAL_SEED_COMMUNICATIONS;
  }
}

function saveToStorage(records: CommunicationRecord[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    window.dispatchEvent(new CustomEvent('mdd:communication_logged', { detail: records }));
  } catch (err) {
    console.error('Failed to save communication record:', err);
  }
}

/** Record a new Call interaction */
export function recordCallCommunication(
  record: Omit<CommunicationRecord, 'id' | 'type' | 'timestamp'> & { timestamp?: string }
): CommunicationRecord {
  const current = loadFromStorage();
  const newEntry: CommunicationRecord = {
    ...record,
    id: `call-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'CALL',
    timestamp: record.timestamp || new Date().toISOString(),
  };
  const updated = [newEntry, ...current];
  saveToStorage(updated);
  return newEntry;
}

/** Record a new WhatsApp interaction */
export function recordWhatsAppCommunication(
  record: Omit<CommunicationRecord, 'id' | 'type' | 'timestamp'> & { timestamp?: string }
): CommunicationRecord {
  const current = loadFromStorage();
  const newEntry: CommunicationRecord = {
    ...record,
    id: `wa-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'WHATSAPP',
    timestamp: record.timestamp || new Date().toISOString(),
  };
  const updated = [newEntry, ...current];
  saveToStorage(updated);
  return newEntry;
}

/** Get all communications for a given retailer */
export function getCommunicationsForRetailer(retailerId: number): CommunicationRecord[] {
  const all = loadFromStorage();
  return all
    .filter((c) => Number(c.retailerId) === Number(retailerId))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Get all communications across all retailers */
export function getAllCommunications(): CommunicationRecord[] {
  const all = loadFromStorage();
  return [...all].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Compute communication stats for a retailer or overall */
export function getCommunicationStats(retailerId?: number): CommunicationStats {
  const records = retailerId ? getCommunicationsForRetailer(retailerId) : getAllCommunications();

  let totalCalls = 0;
  let adminCalls = 0;
  let rmCalls = 0;
  let totalWhatsApp = 0;
  let adminWhatsApp = 0;
  let rmWhatsApp = 0;
  let lastContactedAt: string | undefined;

  for (const r of records) {
    if (!lastContactedAt || new Date(r.timestamp) > new Date(lastContactedAt)) {
      lastContactedAt = r.timestamp;
    }
    if (r.type === 'CALL') {
      totalCalls++;
      if (r.senderRole === 'ADMIN') adminCalls++;
      else if (r.senderRole === 'RM') rmCalls++;
    } else if (r.type === 'WHATSAPP') {
      totalWhatsApp++;
      if (r.senderRole === 'ADMIN') adminWhatsApp++;
      else if (r.senderRole === 'RM') rmWhatsApp++;
    }
  }

  return {
    totalCalls,
    adminCalls,
    rmCalls,
    totalWhatsApp,
    adminWhatsApp,
    rmWhatsApp,
    lastContactedAt,
  };
}

/** Subscribe to live updates of communications */
export function subscribeToCommunications(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener('mdd:communication_logged', handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('mdd:communication_logged', handler);
    window.removeEventListener('storage', handler);
  };
}
